import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { SearchHistoryItem } from '../hooks/useSearchHistory';

type SearchType = 'semantic' | 'hybrid' | 'keyword';

interface SearchHistoryProps {
  history: SearchHistoryItem[];
  onSelect: (query: string, type: SearchType) => void;
  onRemove: (timestamp: number) => void;
  onClear: () => void;
  isOpen: boolean;
  onClose: () => void;
}

export default function SearchHistory({
  history,
  onSelect,
  onRemove,
  onClear,
  isOpen,
  onClose,
}: SearchHistoryProps) {
  const { t } = useTranslation();

  if (!isOpen || history.length === 0) return null;

  return (
    <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden z-40">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
          {t('searchHistory.recentSearches')}
        </span>
        <button
          onClick={onClear}
          className="text-xs text-slate-500 hover:text-red-600 dark:hover:text-red-400"
        >
          {t('searchHistory.clear')}
        </button>
      </div>
      <ul className="max-h-64 overflow-y-auto">
        {history.slice(0, 10).map((item) => (
          <li
            key={item.timestamp}
            className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer group"
          >
            <button
              onClick={() => {
                onSelect(item.query, item.type);
                onClose();
              }}
              className="flex-1 flex items-center gap-3 text-left"
            >
              <svg
                className="w-4 h-4 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-700 dark:text-slate-300 truncate">
                  {item.query}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-500">
                  {item.type === 'semantic' ? t('searchHistory.semantic') : item.type === 'hybrid' ? t('searchHistory.hybrid') : t('searchHistory.keyword')} •{' '}
                  {item.resultCount} {t('searchHistory.results')} •{' '}
                  {formatRelativeTime(item.timestamp, t)}
                </p>
              </div>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove(item.timestamp);
              }}
              className="p-1 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-opacity"
              title={t('searchHistory.remove')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatRelativeTime(timestamp: number, t: (key: string, options?: Record<string, unknown>) => string): string {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return t('searchHistory.justNow');
  if (minutes < 60) return t('searchHistory.minutesAgo', { count: minutes });
  if (hours < 24) return t('searchHistory.hoursAgo', { count: hours });
  if (days < 7) return t('searchHistory.daysAgo', { count: days });
  const fnLocale = i18next.language === 'pl' ? 'pl-PL' : 'en-US';
  return new Date(timestamp).toLocaleDateString(fnLocale);
}
